import { Stack } from '@etg/wings';
import { useFieldArray, useFormContext } from 'react-hook-form';
import type { TripType } from '@eti/schema-types';
import { DEPARTURE_DATE, DESTINATION, MULTI_BOUND, ORIGIN } from '../constants/formFieldNames';
import { MAX_BOUNDS } from '../constants/multistop';
import type { DateFormat } from '../types/dateFormat';
import AddBoundButton from './AddBoundButton';
import BoundHeader from './BoundHeader';
import BoundNew from './BoundNew';

type BoundsNewProps = {
  dateFormat: DateFormat;
  departureDateErrorMessage: string;
  daysFromTodayForValidDepartureDate: number;
  returnDateErrorMessage: string;
  selectedTripType: typeof TripType.MultiStop | typeof TripType.OneWay | typeof TripType.Return;
  setDepartureDateErrorMessage: (message: string) => void;
  setReturnDateErrorMessage: (message: string) => void;
};

const getKey = (index: number) => `bound-${index}`;

const BoundsNew = ({
  dateFormat,
  departureDateErrorMessage,
  daysFromTodayForValidDepartureDate,
  returnDateErrorMessage,
  selectedTripType,
  setDepartureDateErrorMessage,
  setReturnDateErrorMessage,
}: BoundsNewProps) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: MULTI_BOUND,
  });
  const fieldsWithIds = fields.map((field, index) => ({ field, id: index }));
  const hasRemoveButtons = fieldsWithIds.length > 2;
  const onAdd = () => {
    append({ [ORIGIN]: null, [DESTINATION]: null, [DEPARTURE_DATE]: null });
  };
  const onRemove = (boundId?: number) => {
    remove(boundId);
  };

  return (
    <>
      {fieldsWithIds.map((_, index) => (
        <Stack key={getKey(index)}>
          <BoundHeader boundIndex={index} onRemove={onRemove} />
          <BoundNew
            boundIndex={index}
            dateFormat={dateFormat}
            daysFromTodayForValidDepartureDate={daysFromTodayForValidDepartureDate}
            departureDateErrorMessage={departureDateErrorMessage}
            hasRemoveButtons={hasRemoveButtons}
            onRemove={onRemove}
            returnDateErrorMessage={returnDateErrorMessage}
            selectedTripType={selectedTripType}
            setDepartureDateErrorMessage={setDepartureDateErrorMessage}
            setReturnDateErrorMessage={setReturnDateErrorMessage}
          />
        </Stack>
      ))}
      {fields.length < MAX_BOUNDS && <AddBoundButton onAdd={onAdd} />}
    </>
  );
};

export default BoundsNew;
